import { Tree } from "fluid-framework";
import { useEffect, useRef } from "react";

import {
  hasDuplicateUsers,
  removeAllDuplicateUsers,
} from "../fluid.service.js";
import { useFluid } from "./use-fluid.js";

/**
 * Watches the Fluid SharedTree for duplicate user entries across locations
 * (entrance area, tables, rooms) and in the users[] array.
 *
 * Fluid Framework uses optimistic concurrency — when two clients insert the
 * same userId into a location array concurrently, both `includes()` guards
 * pass locally before either client receives the other's operation. After
 * merge, the userId appears twice. This hook catches and cleans up those
 * duplicates reactively.
 */
export function useFluidDeduplication(): void {
  const { sharedTree } = useFluid();
  const isDeduplicatingRef = useRef(false);
  const pendingDeduplicationTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    const checkAndDeduplicate = () => {
      if (isDeduplicatingRef.current || !hasDuplicateUsers(sharedTree)) {
        return;
      }

      isDeduplicatingRef.current = true;
      try {
        removeAllDuplicateUsers(sharedTree);
      } finally {
        isDeduplicatingRef.current = false;
      }
    };

    const scheduleDeduplication = () => {
      if (pendingDeduplicationTimeoutRef.current) {
        return;
      }

      // SharedTree forbids writes during treeChanged/nodeChanged notifications.
      // Deferring to the next tick keeps dedup safe while still reacting quickly.
      pendingDeduplicationTimeoutRef.current = setTimeout(() => {
        pendingDeduplicationTimeoutRef.current = null;
        checkAndDeduplicate();
      }, 0);
    };

    const unsubscribe = Tree.on(
      sharedTree.root,
      "treeChanged",
      scheduleDeduplication
    );

    checkAndDeduplicate();

    return () => {
      unsubscribe();
      if (pendingDeduplicationTimeoutRef.current) {
        clearTimeout(pendingDeduplicationTimeoutRef.current);
        pendingDeduplicationTimeoutRef.current = null;
      }
    };
  }, [sharedTree]);
}
